/**
 * Drawings library page (served at / and /f/<folderId>): folders and
 * drawings with thumbnails; open / create / rename / duplicate / move (also
 * by drag and drop) / delete / import and ZIP export of a selection.
 */

import { loadFromBlob } from "@excalidraw/excalidraw";
import {
  alertTriangleIcon,
  chevronLeftIcon,
  DuplicateIcon,
  downloadIcon,
  historyIcon,
  LoadIcon,
  pencilIcon,
  PlusIcon,
  searchIcon,
  TrashIcon,
  UndoIcon,
} from "@excalidraw/excalidraw/components/icons";
import { THEME } from "@excalidraw/common";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { STORAGE_KEYS } from "../app_constants";

import {
  ConflictDialog,
  DeleteFoldersDialog,
  HistoryDialog,
  Modal,
  MoveToDialog,
  RenameDialog,
  SettingsDialog,
  SyncStatusIndicator,
  downloadBlob,
  folderIcon,
  formatDateTime,
  logOutIcon,
  sanitizeFileName,
  settingsGearIcon,
} from "./components";
import {
  createDrawing,
  duplicateDrawing,
  loadDrawing,
  purgeDrawings,
  refreshThumbnail,
  renameDrawing,
  renderDrawingPng,
  restoreDrawings,
  serializeDrawing,
  trashDrawings,
} from "./drawings";
import {
  getDrawingPath,
  getLastOpenedDrawingId,
  getLibraryFolderFromUrl,
  getLibraryPath,
  isLibraryPage,
  openDrawing,
} from "./editor";
import {
  buildFolderTree,
  canMoveFoldersInto,
  createFolder,
  deleteFolders,
  getFolderContents,
  moveItems,
  renameFolder,
} from "./folders";
import { lt } from "./i18n";
import { logOut, useProxySession } from "./session";
import { DrawingStore, onLibraryChange } from "./store";
import { SyncEngine } from "./sync";
import { createZip } from "./zip";

import type { FolderTree } from "./folders";
import type { DrawingId, DrawingMeta, Folder, FolderId } from "./types";
import type { ZipEntry } from "./zip";

type View = "all" | "trash";
type SortKey = "updated" | "created" | "name";

/** selection / drag keys: "d:<drawing id>" or "f:<folder id>" */
type ItemKey = string;
const drawingKey = (id: DrawingId): ItemKey => `d:${id}`;
const folderKey = (id: FolderId): ItemKey => `f:${id}`;

type Items = { drawingIds: DrawingId[]; folderIds: FolderId[] };

const splitKeys = (keys: Iterable<ItemKey>): Items => {
  const items: Items = { drawingIds: [], folderIds: [] };
  for (const key of keys) {
    (key.startsWith("d:") ? items.drawingIds : items.folderIds).push(
      key.slice(2),
    );
  }
  return items;
};

/** drop target key for the root */
const ROOT_TARGET = "root";

const SORT_PREFERENCE_KEY = "excalidraw-library-sort";

const readSortPreference = (): SortKey => {
  try {
    const value = localStorage.getItem(SORT_PREFERENCE_KEY);
    if (value === "updated" || value === "created" || value === "name") {
      return value;
    }
  } catch {
    // ignore
  }
  return "updated";
};

const useLibraryTheme = () => {
  const [theme, setTheme] = useState<"light" | "dark">(THEME.LIGHT);
  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    const update = () => {
      let preference: string | null = null;
      try {
        preference = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_THEME);
      } catch {
        // ignore
      }
      if (preference === THEME.DARK || preference === THEME.LIGHT) {
        setTheme(preference);
      } else if (preference === "system") {
        setTheme(media?.matches ? THEME.DARK : THEME.LIGHT);
      } else {
        setTheme(THEME.LIGHT);
      }
    };
    update();
    media?.addEventListener("change", update);
    window.addEventListener("storage", update);
    return () => {
      media?.removeEventListener("change", update);
      window.removeEventListener("storage", update);
    };
  }, []);
  return theme;
};

const fileNameWithoutExtension = (name: string) =>
  name.replace(/\.(excalidraw|json|png|svg)$/i, "");

const folderPathLabel = (tree: FolderTree, folderId: FolderId | null) => {
  const path = tree.pathOf(tree.resolve(folderId));
  return path.length
    ? path.map((folder) => folder.name).join(" / ")
    : lt("rootFolder");
};

/** drag and drop handlers of a drop target */
type DropProps = {
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
};

const CardThumbnail = ({ meta }: { meta: DrawingMeta }) =>
  meta.thumbnail ? (
    <img src={meta.thumbnail} alt="" loading="lazy" draggable={false} />
  ) : (
    <span className="exdl-card__placeholder">
      {meta.elementCount ? "…" : ""}
    </span>
  );

const SelectCheckbox = ({
  label,
  isSelected,
  onToggle,
}: {
  label: string;
  isSelected: boolean;
  onToggle: (shiftKey: boolean) => void;
}) => (
  <label className="exdl-card__check" title={label}>
    <input
      type="checkbox"
      aria-label={label}
      checked={isSelected}
      onChange={() => {}}
      onClick={(event) => onToggle(event.shiftKey)}
    />
  </label>
);

const DrawingCard = ({
  meta,
  pathLabel,
  isSelected,
  isCurrent,
  isDragging,
  onToggleSelect,
  onDragStart,
  onDragEnd,
  onRename,
  onDuplicate,
  onDownload,
  onTrash,
  onRestore,
  onPurge,
  onHistory,
  onResolveConflict,
}: {
  meta: DrawingMeta;
  /** folder path, shown in search results and trash */
  pathLabel: string | null;
  isSelected: boolean;
  isCurrent: boolean;
  isDragging: boolean;
  onToggleSelect: (shiftKey: boolean) => void;
  onDragStart: (event: React.DragEvent) => void;
  onDragEnd: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDownload: () => void;
  onTrash: () => void;
  onRestore: () => void;
  onPurge: () => void;
  onHistory: () => void;
  onResolveConflict: () => void;
}) => (
  <li
    className={clsx("exdl-card", {
      "exdl-card--selected": isSelected,
      "exdl-card--current": isCurrent,
      "exdl-card--dragging": isDragging,
    })}
    draggable={!meta.deleted}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
  >
    {meta.deleted ? (
      <button
        type="button"
        className="exdl-card__preview"
        onClick={onRestore}
        title={lt("restore")}
      >
        <CardThumbnail meta={meta} />
      </button>
    ) : (
      <a
        className="exdl-card__preview"
        href={getDrawingPath(meta.id)}
        title={lt("open")}
        draggable={false}
      >
        <CardThumbnail meta={meta} />
      </a>
    )}
    <SelectCheckbox
      label={meta.name}
      isSelected={isSelected}
      onToggle={onToggleSelect}
    />
    <div className="exdl-card__badges">
      {meta.conflict && (
        <button
          type="button"
          className="exdl-badge exdl-badge--warning"
          title={lt("conflictTitle")}
          onClick={onResolveConflict}
        >
          <span className="exdl-icon">{alertTriangleIcon}</span>
          {lt("conflict")}
        </button>
      )}
      {isCurrent && <span className="exdl-badge">{lt("currentDrawing")}</span>}
    </div>
    <div className="exdl-card__info">
      <div className="exdl-card__name" title={meta.name}>
        {meta.name}
      </div>
      {pathLabel && (
        <div className="exdl-card__path" title={pathLabel}>
          <span className="exdl-icon">{folderIcon}</span>
          {pathLabel}
        </div>
      )}
      <div className="exdl-card__meta">
        {formatDateTime(meta.updatedAt)}
        {meta.dirty && (
          <span
            className="exdl-card__dirty"
            title={lt("notSynced")}
            aria-label={lt("notSynced")}
          />
        )}
      </div>
    </div>
    <div className="exdl-card__actions">
      {meta.deleted ? (
        <>
          <button
            type="button"
            className="exdl-icon-button"
            title={lt("restore")}
            onClick={onRestore}
          >
            {UndoIcon}
          </button>
          <button
            type="button"
            className="exdl-icon-button exdl-icon-button--danger"
            title={lt("deleteForever")}
            onClick={onPurge}
          >
            {TrashIcon}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="exdl-icon-button"
            title={lt("rename")}
            onClick={onRename}
          >
            {pencilIcon}
          </button>
          <button
            type="button"
            className="exdl-icon-button"
            title={lt("duplicate")}
            onClick={onDuplicate}
          >
            {DuplicateIcon}
          </button>
          <button
            type="button"
            className="exdl-icon-button"
            title={lt("download")}
            onClick={onDownload}
          >
            {downloadIcon}
          </button>
          <button
            type="button"
            className="exdl-icon-button"
            title={lt("versionHistory")}
            onClick={onHistory}
          >
            {historyIcon}
          </button>
          <button
            type="button"
            className="exdl-icon-button exdl-icon-button--danger"
            title={lt("moveToTrash")}
            onClick={onTrash}
          >
            {TrashIcon}
          </button>
        </>
      )}
    </div>
  </li>
);

type FolderSummary = {
  /** drawings inside, sub-folders included */
  drawingCount: number;
  folderCount: number;
  /** thumbnails of the most recent drawings directly inside */
  previews: string[];
};

const FolderCard = ({
  folder,
  summary,
  pathLabel,
  isSelected,
  isDragging,
  isDropTarget,
  dropProps,
  onOpen,
  onToggleSelect,
  onDragStart,
  onDragEnd,
  onRename,
  onDelete,
}: {
  folder: Folder;
  summary: FolderSummary;
  pathLabel: string | null;
  isSelected: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  dropProps: DropProps;
  onOpen: () => void;
  onToggleSelect: (shiftKey: boolean) => void;
  onDragStart: (event: React.DragEvent) => void;
  onDragEnd: () => void;
  onRename: () => void;
  onDelete: () => void;
}) => (
  <li
    className={clsx("exdl-card", "exdl-card--folder", {
      "exdl-card--selected": isSelected,
      "exdl-card--dragging": isDragging,
      "exdl-card--drop-target": isDropTarget,
    })}
    draggable
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    {...dropProps}
  >
    <a
      className="exdl-card__preview exdl-folder-preview"
      href={getLibraryPath(folder.id)}
      title={lt("open")}
      draggable={false}
      onClick={(event) => {
        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      {summary.previews.length ? (
        <span
          className={clsx(
            "exdl-folder-preview__grid",
            `exdl-folder-preview__grid--${summary.previews.length}`,
          )}
        >
          {summary.previews.map((thumbnail, index) => (
            <img key={index} src={thumbnail} alt="" draggable={false} />
          ))}
        </span>
      ) : (
        <span className="exdl-folder-preview__icon">{folderIcon}</span>
      )}
    </a>
    <SelectCheckbox
      label={folder.name}
      isSelected={isSelected}
      onToggle={onToggleSelect}
    />
    <div className="exdl-card__info">
      <div className="exdl-card__name" title={folder.name}>
        <span className="exdl-icon">{folderIcon}</span>
        {folder.name}
      </div>
      {pathLabel && (
        <div className="exdl-card__path" title={pathLabel}>
          {pathLabel}
        </div>
      )}
      <div className="exdl-card__meta">
        {lt("folderSummary", {
          drawings: summary.drawingCount,
          folders: summary.folderCount,
        })}
      </div>
    </div>
    <div className="exdl-card__actions">
      <button
        type="button"
        className="exdl-icon-button"
        title={lt("rename")}
        onClick={onRename}
      >
        {pencilIcon}
      </button>
      <button
        type="button"
        className="exdl-icon-button exdl-icon-button--danger"
        title={lt("delete")}
        onClick={onDelete}
      >
        {TrashIcon}
      </button>
    </div>
  </li>
);

type DeleteRequest = {
  folderIds: FolderId[];
  /** drawings selected together with the folders: moved to the trash */
  drawingIds: DrawingId[];
};

export const LibraryPage = () => {
  const theme = useLibraryTheme();
  const [drawings, setDrawings] = useState<DrawingMeta[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [folderIdFromUrl, setFolderIdFromUrl] = useState(
    getLibraryFolderFromUrl,
  );
  const [view, setView] = useState<View>("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>(readSortPreference);
  const [selected, setSelected] = useState<Set<ItemKey>>(new Set());
  const [includePng, setIncludePng] = useState(false);
  const [busyMessage, setBusyMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<DrawingMeta | null>(null);
  const [renamingFolder, setRenamingFolder] = useState<Folder | null>(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [purging, setPurging] = useState<DrawingId[] | null>(null);
  const [historyId, setHistoryId] = useState<DrawingId | null>(null);
  const [conflictId, setConflictId] = useState<DrawingId | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const proxySession = useProxySession();
  const [moving, setMoving] = useState<Items | null>(null);
  const [deleting, setDeleting] = useState<DeleteRequest | null>(null);
  const [dragging, setDragging] = useState<Set<ItemKey> | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const lastClickedRef = useRef<ItemKey | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const thumbnailsInProgress = useRef(new Set<DrawingId>());
  const lastOpenedId = getLastOpenedDrawingId();

  const tree = useMemo(() => buildFolderTree(folders), [folders]);
  const currentFolderId = tree.resolve(folderIdFromUrl);
  const isFolderMissing = !!folderIdFromUrl && !currentFolderId;
  const currentFolder = currentFolderId
    ? tree.byId.get(currentFolderId)!
    : null;
  const isSearching = view === "all" && !!query.trim();

  const reload = useCallback(async () => {
    const [metas, folderList] = await Promise.all([
      DrawingStore.listMeta(),
      DrawingStore.listFolders(),
    ]);
    setDrawings(metas.filter((meta) => !meta.purged));
    setFolders(folderList);
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (window.location.pathname === "/library") {
      // legacy URL
      window.history.replaceState({}, "", getLibraryPath(null));
    }
    reload();
    SyncEngine.start();
    const unsubscribe = onLibraryChange(() => reload());
    const onFocus = () => reload();
    // e.g. a room or legacy drawing link pasted while on the library
    const onHashChange = () => {
      if (!isLibraryPage()) {
        window.location.reload();
      }
    };
    const onPopState = () => {
      if (isLibraryPage()) {
        setFolderIdFromUrl(getLibraryFolderFromUrl());
        setSelected(new Set());
      } else {
        window.location.reload();
      }
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("popstate", onPopState);
    return () => {
      unsubscribe();
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("popstate", onPopState);
    };
  }, [reload]);

  useEffect(() => {
    document.title = `${
      currentFolder?.name ?? lt("libraryTitle")
    } - Excalidraw`;
  }, [currentFolder?.name]);

  const navigateToFolder = (folderId: FolderId | null) => {
    window.history.pushState({}, "", getLibraryPath(folderId));
    setFolderIdFromUrl(folderId);
    setSelected(new Set());
    setView("all");
    setQuery("");
  };

  // drawings saved without a thumbnail (e.g. merged after a conflict)
  useEffect(() => {
    const missing = drawings.filter(
      (meta) =>
        !meta.thumbnail &&
        meta.elementCount > 0 &&
        !meta.deleted &&
        !thumbnailsInProgress.current.has(meta.id),
    );
    (async () => {
      for (const meta of missing) {
        thumbnailsInProgress.current.add(meta.id);
        try {
          await refreshThumbnail(meta.id, "library");
        } catch (error) {
          console.warn(error);
        }
      }
    })();
  }, [drawings]);

  useEffect(() => {
    try {
      localStorage.setItem(SORT_PREFERENCE_KEY, sort);
    } catch {
      // ignore
    }
  }, [sort]);

  const liveDrawings = useMemo(
    () => drawings.filter((meta) => !meta.deleted),
    [drawings],
  );
  const trashCount = drawings.length - liveDrawings.length;

  const folderSummaries = useMemo(() => {
    const summaries = new Map<FolderId, FolderSummary>();
    for (const id of tree.byId.keys()) {
      summaries.set(id, {
        drawingCount: 0,
        folderCount: tree.childrenOf(id).length,
        previews: [],
      });
    }
    const byRecency = [...liveDrawings].sort(
      (a, b) => b.updatedAt - a.updatedAt,
    );
    for (const meta of byRecency) {
      const folderId = tree.resolve(meta.folderId);
      if (!folderId) {
        continue;
      }
      const direct = summaries.get(folderId)!;
      if (meta.thumbnail && direct.previews.length < 4) {
        direct.previews.push(meta.thumbnail);
      }
      for (const ancestor of tree.pathOf(folderId)) {
        summaries.get(ancestor.id)!.drawingCount++;
      }
    }
    return summaries;
  }, [tree, liveDrawings]);

  const visibleFolders = useMemo(() => {
    if (view === "trash") {
      return [];
    }
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return normalizedQuery
      ? [...tree.byId.values()]
          .filter((folder) =>
            folder.name.toLocaleLowerCase().includes(normalizedQuery),
          )
          .sort((a, b) => a.name.localeCompare(b.name))
      : tree.childrenOf(currentFolderId);
  }, [tree, view, query, currentFolderId]);

  const visibleDrawings = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const list =
      view === "trash"
        ? drawings.filter((meta) => meta.deleted)
        : normalizedQuery
        ? liveDrawings.filter((meta) =>
            meta.name.toLocaleLowerCase().includes(normalizedQuery),
          )
        : liveDrawings.filter(
            (meta) => tree.resolve(meta.folderId) === currentFolderId,
          );
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return [...list].sort((a, b) => {
      switch (sort) {
        case "name":
          return collator.compare(a.name, b.name);
        case "created":
          return b.createdAt - a.createdAt;
        default:
          return b.updatedAt - a.updatedAt;
      }
    });
  }, [drawings, liveDrawings, view, query, sort, tree, currentFolderId]);

  const visibleKeys = useMemo(
    () => [
      ...visibleFolders.map((folder) => folderKey(folder.id)),
      ...visibleDrawings.map((meta) => drawingKey(meta.id)),
    ],
    [visibleFolders, visibleDrawings],
  );

  // keep the selection limited to what is shown
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(visibleKeys);
      const next = new Set([...prev].filter((key) => visible.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleKeys]);

  const selectedKeys = useMemo(
    () => visibleKeys.filter((key) => selected.has(key)),
    [visibleKeys, selected],
  );
  const selectedItems = useMemo(() => splitKeys(selectedKeys), [selectedKeys]);
  const allSelected =
    visibleKeys.length > 0 && selectedKeys.length === visibleKeys.length;

  const toggleSelect = (key: ItemKey, shiftKey: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const lastKey = lastClickedRef.current;
      if (shiftKey && lastKey && lastKey !== key) {
        const [from, to] = [
          visibleKeys.indexOf(lastKey),
          visibleKeys.indexOf(key),
        ].sort((a, b) => a - b);
        if (from >= 0) {
          visibleKeys.slice(from, to + 1).forEach((k) => next.add(k));
          return next;
        }
      }
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    lastClickedRef.current = key;
  };

  const run = async (message: string | null, task: () => Promise<void>) => {
    setBusyMessage(message);
    try {
      await task();
    } catch (error: any) {
      console.error(error);
      setErrorMessage(error?.message ?? String(error));
    } finally {
      setBusyMessage(null);
      reload();
    }
  };

  // ---------------------------------------------------------------------------
  // drag and drop
  // ---------------------------------------------------------------------------

  const startDrag = (key: ItemKey, event: React.DragEvent) => {
    // dragging a selected item moves the whole selection
    const keys = selected.has(key) ? selectedKeys : [key];
    setDragging(new Set(keys));
    event.dataTransfer.effectAllowed = "move";
    // required by Firefox to start dragging
    event.dataTransfer.setData("text/plain", keys.join("\n"));
  };

  const endDrag = () => {
    setDragging(null);
    setDropTarget(null);
  };

  const canDropOn = (targetId: FolderId | null) => {
    if (!dragging) {
      return false;
    }
    const items = splitKeys(dragging);
    return canMoveFoldersInto(tree, items.folderIds, targetId);
  };

  const dropProps = (targetId: FolderId | null): DropProps => {
    const target = targetId ?? ROOT_TARGET;
    return {
      onDragOver: (event) => {
        if (canDropOn(targetId)) {
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          if (dropTarget !== target) {
            setDropTarget(target);
          }
        }
      },
      onDragLeave: (event) => {
        if (
          dropTarget === target &&
          !event.currentTarget.contains(event.relatedTarget as Node | null)
        ) {
          setDropTarget(null);
        }
      },
      onDrop: (event) => {
        event.preventDefault();
        if (dragging && canDropOn(targetId)) {
          const items = splitKeys(dragging);
          run(null, async () => {
            await moveItems(items, targetId);
            setSelected(new Set());
          });
        }
        endDrag();
      },
    };
  };

  // ---------------------------------------------------------------------------
  // actions
  // ---------------------------------------------------------------------------

  const downloadDrawing = (id: DrawingId) =>
    run(null, async () => {
      const drawing = await loadDrawing(id);
      if (drawing) {
        downloadBlob(
          new Blob([serializeDrawing(drawing)], { type: "application/json" }),
          `${sanitizeFileName(drawing.meta.name)}.excalidraw`,
        );
      }
    });

  /** ZIP of the selection; folders keep their structure (relative to the
   * folder being shown) */
  const exportZip = (items: Items) =>
    run(lt("exporting"), async () => {
      const metas = new Map<DrawingId, DrawingMeta>();
      for (const id of items.drawingIds) {
        const meta = liveDrawings.find((m) => m.id === id);
        if (meta) {
          metas.set(id, meta);
        }
      }
      for (const id of items.folderIds) {
        getFolderContents(tree, drawings, id).drawings.forEach((meta) =>
          metas.set(meta.id, meta),
        );
      }
      const directoryOf = (meta: DrawingMeta) => {
        const path = tree.pathOf(tree.resolve(meta.folderId));
        const index = path.findIndex((f) => f.id === currentFolderId);
        return (currentFolderId && index >= 0 ? path.slice(index + 1) : path)
          .map((folder) => `${sanitizeFileName(folder.name)}/`)
          .join("");
      };

      const encoder = new TextEncoder();
      const entries: ZipEntry[] = [];
      const usedNames = new Set<string>();
      for (const meta of metas.values()) {
        const drawing = await loadDrawing(meta.id);
        if (!drawing) {
          continue;
        }
        const directory = directoryOf(meta);
        const baseName = sanitizeFileName(meta.name);
        let name = baseName;
        for (
          let i = 2;
          usedNames.has(`${directory}${name}`.toLowerCase());
          i++
        ) {
          name = `${baseName} (${i})`;
        }
        usedNames.add(`${directory}${name}`.toLowerCase());
        const date = new Date(meta.updatedAt);
        entries.push({
          name: `${directory}${name}.excalidraw`,
          data: encoder.encode(serializeDrawing(drawing)),
          date,
        });
        if (includePng && meta.elementCount) {
          const png = await renderDrawingPng(drawing);
          entries.push({
            name: `${directory}${name}.png`,
            data: new Uint8Array(await png.arrayBuffer()),
            date,
          });
        }
      }
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      const prefix = currentFolder
        ? sanitizeFileName(currentFolder.name)
        : "excalidraw";
      downloadBlob(createZip(entries), `${prefix}-${stamp}.zip`);
    });

  const importFiles = (files: File[]) =>
    run(null, async () => {
      const failed: string[] = [];
      for (const file of files) {
        try {
          const data = await loadFromBlob(file, null, null);
          await createDrawing({
            name: fileNameWithoutExtension(file.name),
            elements: data.elements,
            appState: data.appState,
            files: data.files,
            folderId: currentFolderId,
          });
        } catch (error) {
          console.warn(error);
          failed.push(file.name);
        }
      }
      if (failed.length) {
        setErrorMessage(
          failed.map((name) => lt("importFailed", { name })).join("\n"),
        );
      }
    });

  /** deletion of the selection: drawings go to the trash, folders ask */
  const deleteItems = (items: Items) => {
    if (items.folderIds.length) {
      setDeleting({ folderIds: items.folderIds, drawingIds: items.drawingIds });
    } else if (items.drawingIds.length) {
      run(null, () => trashDrawings(items.drawingIds));
    }
  };

  const deletingSummary = useMemo(() => {
    if (!deleting) {
      return null;
    }
    const drawingIds = new Set<DrawingId>();
    const folderIds = new Set<FolderId>();
    for (const id of deleting.folderIds) {
      const contents = getFolderContents(tree, drawings, id);
      contents.drawings.forEach((meta) => drawingIds.add(meta.id));
      contents.folders.forEach((folderId) => folderIds.add(folderId));
    }
    const parents = new Set(deleting.folderIds.map((id) => tree.parentOf(id)));
    const parentId = parents.size === 1 ? [...parents][0] : undefined;
    return {
      folderNames: deleting.folderIds
        .map((id) => tree.byId.get(id)?.name)
        .filter((name): name is string => !!name),
      drawingCount: drawingIds.size,
      folderCount: folderIds.size,
      parentName:
        parentId === undefined
          ? lt("parentFolders")
          : parentId
          ? tree.byId.get(parentId)!.name
          : lt("rootFolder"),
    };
  }, [deleting, tree, drawings]);

  // ---------------------------------------------------------------------------
  // render
  // ---------------------------------------------------------------------------

  const breadcrumbs = currentFolderId ? tree.pathOf(currentFolderId) : [];
  const showPaths = isSearching || view === "trash";
  const isEmpty = !visibleFolders.length && !visibleDrawings.length;

  return (
    <div
      className={clsx("exdl-page", { "exdl-theme--dark": theme === "dark" })}
    >
      <header className="exdl-header">
        {lastOpenedId && (
          <a
            className="exdl-header__back"
            href={getDrawingPath(lastOpenedId)}
            title={lt("backToEditor")}
          >
            <span className="exdl-icon">{chevronLeftIcon}</span>
            <span className="exdl-header__back-label">
              {lt("backToEditor")}
            </span>
          </a>
        )}
        <h1 className="exdl-header__title">
          {currentFolder?.name ?? lt("libraryTitle")}
        </h1>
        <SyncStatusIndicator
          onClick={() => {
            const conflicted = drawings.find(
              (meta) => meta.conflict && !meta.purged,
            );
            if (conflicted) {
              setConflictId(conflicted.id);
            } else {
              SyncEngine.syncNow();
            }
          }}
        />
        <button
          type="button"
          className="exdl-icon-button exdl-header__settings"
          title={lt("settings")}
          aria-label={lt("settings")}
          onClick={() => setIsSettingsOpen(true)}
        >
          {settingsGearIcon}
        </button>
        {proxySession && (
          <button
            type="button"
            className="exdl-icon-button"
            title={lt("logOut", { user: proxySession.user })}
            aria-label={lt("logOut", { user: proxySession.user })}
            onClick={() => logOut(proxySession)}
          >
            {logOutIcon}
          </button>
        )}
      </header>

      <div className="exdl-toolbar">
        <button
          type="button"
          className="exdl-button exdl-button--primary"
          onClick={() => openDrawing("new", currentFolderId)}
        >
          <span className="exdl-icon">{PlusIcon}</span>
          {lt("newDrawing")}
        </button>
        <button
          type="button"
          className="exdl-button"
          onClick={() => setIsCreatingFolder(true)}
        >
          <span className="exdl-icon">{folderIcon}</span>
          {lt("newFolder")}
        </button>
        <button
          type="button"
          className="exdl-button"
          onClick={() => importInputRef.current?.click()}
        >
          <span className="exdl-icon">{LoadIcon}</span>
          {lt("importFiles")}
        </button>
        <input
          ref={importInputRef}
          type="file"
          multiple
          hidden
          accept=".excalidraw,.json,application/json,image/png,image/svg+xml"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = "";
            if (files.length) {
              importFiles(files);
            }
          }}
        />
        <label className="exdl-search">
          <span className="exdl-icon">{searchIcon}</span>
          <input
            type="search"
            placeholder={lt("search")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <select
          className="exdl-select"
          value={sort}
          onChange={(event) => setSort(event.target.value as SortKey)}
        >
          <option value="updated">{lt("sortUpdated")}</option>
          <option value="created">{lt("sortCreated")}</option>
          <option value="name">{lt("sortName")}</option>
        </select>
      </div>

      <nav className="exdl-tabs">
        <button
          type="button"
          className={clsx("exdl-tab", { "exdl-tab--active": view === "all" })}
          onClick={() => setView("all")}
        >
          {lt("allDrawings")}
          <span className="exdl-tab__count">{liveDrawings.length}</span>
        </button>
        <button
          type="button"
          className={clsx("exdl-tab", { "exdl-tab--active": view === "trash" })}
          onClick={() => setView("trash")}
        >
          {lt("trash")}
          <span className="exdl-tab__count">{trashCount}</span>
        </button>
      </nav>

      {view === "all" && !isSearching && (
        <ol className="exdl-breadcrumbs">
          {[null, ...breadcrumbs].map((folder, index) => {
            const targetId = folder?.id ?? null;
            const isLast = index === breadcrumbs.length;
            return (
              <li
                key={targetId ?? ROOT_TARGET}
                className={clsx("exdl-breadcrumbs__item", {
                  "exdl-breadcrumbs__item--drop-target":
                    dropTarget === (targetId ?? ROOT_TARGET),
                })}
                {...dropProps(targetId)}
              >
                {isLast ? (
                  <span aria-current="page">
                    {folder?.name ?? lt("rootFolder")}
                  </span>
                ) : (
                  <a
                    href={getLibraryPath(targetId)}
                    onClick={(event) => {
                      event.preventDefault();
                      navigateToFolder(targetId);
                    }}
                  >
                    {folder?.name ?? lt("rootFolder")}
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <div className="exdl-selection">
        <label className="exdl-checkbox">
          <input
            type="checkbox"
            checked={allSelected}
            disabled={!visibleKeys.length}
            onChange={() =>
              setSelected(allSelected ? new Set() : new Set(visibleKeys))
            }
          />
          {selectedKeys.length
            ? lt("selectedCount", { count: selectedKeys.length })
            : lt("selectAll")}
        </label>
        <div className="exdl-selection__actions">
          {view === "all" ? (
            <>
              <label className="exdl-checkbox">
                <input
                  type="checkbox"
                  checked={includePng}
                  onChange={(event) => setIncludePng(event.target.checked)}
                />
                {lt("includePng")}
              </label>
              <button
                type="button"
                className="exdl-button"
                disabled={!selectedKeys.length}
                onClick={() => exportZip(selectedItems)}
              >
                <span className="exdl-icon">{downloadIcon}</span>
                {lt("exportZip")}
              </button>
              <button
                type="button"
                className="exdl-button"
                disabled={!selectedKeys.length}
                onClick={() => setMoving(selectedItems)}
              >
                <span className="exdl-icon">{folderIcon}</span>
                {lt("moveTo")}
              </button>
              <button
                type="button"
                className="exdl-button exdl-button--danger"
                disabled={!selectedKeys.length}
                onClick={() => deleteItems(selectedItems)}
              >
                <span className="exdl-icon">{TrashIcon}</span>
                {lt("delete")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="exdl-button"
                disabled={!selectedKeys.length}
                onClick={() =>
                  run(null, () => restoreDrawings(selectedItems.drawingIds))
                }
              >
                <span className="exdl-icon">{UndoIcon}</span>
                {lt("restore")}
              </button>
              <button
                type="button"
                className="exdl-button exdl-button--danger"
                disabled={!selectedKeys.length}
                onClick={() => setPurging(selectedItems.drawingIds)}
              >
                <span className="exdl-icon">{TrashIcon}</span>
                {lt("deleteForever")}
              </button>
            </>
          )}
        </div>
      </div>

      <main className="exdl-content">
        {isLoaded && isFolderMissing && view === "all" && !isSearching && (
          <p className="exdl-empty">
            {lt("folderNotFound")}{" "}
            <a
              href={getLibraryPath(null)}
              onClick={(event) => {
                event.preventDefault();
                navigateToFolder(null);
              }}
            >
              {lt("rootFolder")}
            </a>
          </p>
        )}
        {isLoaded && isEmpty && !isFolderMissing && (
          <p className="exdl-empty">
            {isSearching
              ? lt("noResults")
              : view === "trash"
              ? lt("emptyTrash")
              : currentFolderId
              ? lt("emptyFolder")
              : lt("emptyLibrary")}
          </p>
        )}
        <ul className="exdl-grid">
          {visibleFolders.map((folder) => {
            const key = folderKey(folder.id);
            return (
              <FolderCard
                key={key}
                folder={folder}
                summary={folderSummaries.get(folder.id)!}
                pathLabel={
                  isSearching
                    ? folderPathLabel(tree, tree.parentOf(folder.id))
                    : null
                }
                isSelected={selected.has(key)}
                isDragging={!!dragging?.has(key)}
                isDropTarget={dropTarget === folder.id}
                dropProps={dropProps(folder.id)}
                onOpen={() => navigateToFolder(folder.id)}
                onToggleSelect={(shiftKey) => toggleSelect(key, shiftKey)}
                onDragStart={(event) => startDrag(key, event)}
                onDragEnd={endDrag}
                onRename={() => setRenamingFolder(folder)}
                onDelete={() =>
                  setDeleting({ folderIds: [folder.id], drawingIds: [] })
                }
              />
            );
          })}
          {visibleDrawings.map((meta) => {
            const key = drawingKey(meta.id);
            return (
              <DrawingCard
                key={key}
                meta={meta}
                pathLabel={
                  showPaths ? folderPathLabel(tree, meta.folderId) : null
                }
                isSelected={selected.has(key)}
                isCurrent={meta.id === lastOpenedId}
                isDragging={!!dragging?.has(key)}
                onToggleSelect={(shiftKey) => toggleSelect(key, shiftKey)}
                onDragStart={(event) => startDrag(key, event)}
                onDragEnd={endDrag}
                onRename={() => setRenaming(meta)}
                onDuplicate={() =>
                  run(null, async () => {
                    await duplicateDrawing(meta.id);
                  })
                }
                onDownload={() => downloadDrawing(meta.id)}
                onTrash={() => run(null, () => trashDrawings([meta.id]))}
                onRestore={() => run(null, () => restoreDrawings([meta.id]))}
                onPurge={() => setPurging([meta.id])}
                onHistory={() => setHistoryId(meta.id)}
                onResolveConflict={() => setConflictId(meta.id)}
              />
            );
          })}
        </ul>
      </main>

      {busyMessage && <div className="exdl-toast">{busyMessage}</div>}

      {renaming && (
        <RenameDialog
          initialName={renaming.name}
          onClose={() => setRenaming(null)}
          onSubmit={(name) => {
            const { id } = renaming;
            setRenaming(null);
            run(null, () => renameDrawing(id, name));
          }}
        />
      )}

      {renamingFolder && (
        <RenameDialog
          title={lt("renameFolderTitle")}
          label={lt("folderName")}
          initialName={renamingFolder.name}
          onClose={() => setRenamingFolder(null)}
          onSubmit={(name) => {
            const { id } = renamingFolder;
            setRenamingFolder(null);
            run(null, () => renameFolder(id, name));
          }}
        />
      )}

      {isCreatingFolder && (
        <RenameDialog
          title={lt("newFolder")}
          label={lt("folderName")}
          initialName={lt("newFolderName")}
          onClose={() => setIsCreatingFolder(false)}
          onSubmit={(name) => {
            setIsCreatingFolder(false);
            run(null, async () => {
              await createFolder(name, currentFolderId);
            });
          }}
        />
      )}

      {moving && (
        <MoveToDialog
          tree={tree}
          movingFolderIds={moving.folderIds}
          currentFolderId={currentFolderId}
          onClose={() => setMoving(null)}
          onSubmit={(targetId) => {
            const items = moving;
            setMoving(null);
            run(null, async () => {
              await moveItems(items, targetId);
              setSelected(new Set());
            });
          }}
        />
      )}

      {deleting && deletingSummary && (
        <DeleteFoldersDialog
          {...deletingSummary}
          onClose={() => setDeleting(null)}
          onSubmit={(mode) => {
            const request = deleting;
            setDeleting(null);
            run(null, async () => {
              await trashDrawings(request.drawingIds);
              await deleteFolders(request.folderIds, mode);
              setSelected(new Set());
            });
          }}
        />
      )}

      {purging && (
        <Modal
          title={lt("confirmPurgeTitle")}
          onClose={() => setPurging(null)}
          actions={
            <>
              <button
                type="button"
                className="exdl-button"
                onClick={() => setPurging(null)}
              >
                {lt("cancel")}
              </button>
              <button
                type="button"
                className="exdl-button exdl-button--danger-solid"
                onClick={() => {
                  const ids = purging;
                  setPurging(null);
                  run(null, () => purgeDrawings(ids));
                }}
              >
                {lt("deleteForever")}
              </button>
            </>
          }
        >
          <p>{lt("confirmPurge", { count: purging.length })}</p>
        </Modal>
      )}

      {historyId && (
        <HistoryDialog id={historyId} onClose={() => setHistoryId(null)} />
      )}

      {conflictId && (
        <ConflictDialog id={conflictId} onClose={() => setConflictId(null)} />
      )}

      {isSettingsOpen && (
        <SettingsDialog onClose={() => setIsSettingsOpen(false)} />
      )}

      {errorMessage && (
        <Modal
          title={lt("error")}
          onClose={() => setErrorMessage(null)}
          actions={
            <button
              type="button"
              className="exdl-button exdl-button--primary"
              onClick={() => setErrorMessage(null)}
            >
              OK
            </button>
          }
        >
          <p className="exdl-pre">{errorMessage}</p>
        </Modal>
      )}
    </div>
  );
};

export default LibraryPage;
